import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CABINET_ROUTES } from 'consts';
import TagCloud from 'components/tag-cloud-lib';
import UseRoleNavigation from 'hooks/useRoleNavigation';
import { getIsClient } from 'store/reducers/general/selectors';
import RangeWords from './range-words';
import styles from './styles.module.scss';

const { TRENDS } = CABINET_ROUTES;

const ranges = [10, 20, 50, 80, 100];

const getRangeWords = (words) => {
  const rangesConfig = [];
  const maxRanges = [];

  const maxCount = Math.max(...words.map(({ count }) => count));
  ranges.forEach((item, index) => {
    const isFirstRange = index === 0;
    const rangMaxValue = Math.floor((item * maxCount) / 100);
    maxRanges.push(rangMaxValue);

    const minThreshold = isFirstRange ? 0 : maxRanges[index - 1];

    const filteredWords = words.filter(({ count }) => {
      return count >= minThreshold && count <= rangMaxValue;
    });

    const mappedWords = filteredWords.map(({ count }) => count);
    const minRangeInMapped = Math.min(...mappedWords) || 0;

    let minRange = isFirstRange ? minRangeInMapped : minThreshold;

    // eslint-disable-next-line radix
    const formattedRange = parseInt(minRange / 10) * 10;

    if (formattedRange && formattedRange !== minThreshold) {
      minRange = formattedRange;
    }

    rangesConfig.push([minRange, rangMaxValue]);
  });

  return rangesConfig;
};

const getWordStyles = (index, isClient, isMobile) => {
  const defaultStyle = {
    fontSize: isMobile ? 12 : 13,
    fontWeight: 400,
    transition: '1s',
    color: isClient ? '#FFFFFF' : '#0B2D40',
  };

  switch (index) {
    case 1:
      return defaultStyle;
    case 2:
      defaultStyle.fontSize = isMobile ? 14 : 16;
      defaultStyle.color = isClient ? '#F7F395' : '#555CFF';
      return defaultStyle;
    case 3:
      defaultStyle.fontSize = isMobile ? 18 : 26;
      defaultStyle.color = '#384CB6';
      return defaultStyle;
    case 4:
      defaultStyle.fontSize = isMobile ? 24 : 32;
      defaultStyle.color = '#7ED5AB';
      defaultStyle.fontWeight = 500;
      return defaultStyle;
    case 5:
      defaultStyle.fontSize = isMobile ? 34 : 50;
      defaultStyle.color = '#49B4F4';
      defaultStyle.fontWeight = 800;
      return defaultStyle;
    default:
      return defaultStyle;
  }
};

const WordCloud = (props) => {
  const {
    initialWords = [],
    words = [],
    printRef,
    search = '',
  } = props;

  const searchString = search?.trim() || '';
  const { navigate } = UseRoleNavigation();
  const isClient = useSelector(getIsClient);
  const isMobile = window.innerWidth <= 768;

  const onClick = (word) => {
    return () => {
      let navigateWord = word;
      if (word.startsWith('#')) {
        navigateWord = word.slice(1);
      }
      navigate(`${TRENDS}?search=${navigateWord}`);
    };
  };

  const rangesConfig = useCallback(() => {
    return getRangeWords(initialWords);
  }, [initialWords])();

  const getWordIndex = (ranges, count) => {
    const index = rangesConfig.findIndex((range) => {
      const [min, max] = range;
      return (count === min && count === max) || (count > min && count <= max);
    });

    return index + 1;
  };

  if (!words.length) {
    return (
      <div className={styles.emptyDataWrapper}>
        <svg width="405" height="287" viewBox="0 0 405 287" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M403.835 190.815C402.75 203.846 398.882 216.395 392.473 227.293C386.075 238.195 377.258 247.417 367.072 254.294C356.802 261.076 345.15 265.421 333.247 266.965C330.277 267.389 327.278 267.552 324.292 267.619C322.8 267.665 321.303 267.585 319.814 267.583L314.952 267.411L295.403 266.69C284.446 266.314 273.488 265.986 262.531 265.672C242.543 261.317 223.03 253.859 205.093 243.278C186.918 232.554 170.27 218.785 156.398 202.408C142.551 186.037 131.363 167.169 123.935 146.797C116.394 126.462 112.649 104.703 112.57 82.9514L112.571 82.9826C112.523 104.733 115.918 126.475 122.482 147.294C129.123 168.097 138.913 188.018 151.733 206.026C164.51 224.045 180.179 240.269 198.3 253.55C203.333 257.248 208.565 260.7 213.943 263.937C193.41 263.371 172.878 262.99 152.345 262.871C136.938 262.759 121.531 262.822 106.124 262.969L99.9115 262.916L97.4677 262.909C96.6588 262.906 95.7978 262.911 95.1463 262.881C93.7469 262.819 92.341 262.85 90.9494 262.698C89.5539 262.594 88.1584 262.492 86.7768 262.28C85.3867 262.128 84.0105 261.892 82.6403 261.627C77.1509 260.611 71.7888 258.949 66.7051 256.654C61.6314 254.353 56.7498 251.581 52.2963 248.204C47.8427 244.834 43.7642 240.963 40.1628 236.688C33.0604 228.066 27.787 217.903 25.2194 206.999C22.5928 196.129 22.5557 184.54 24.9969 173.564C26.2062 168.061 28.1318 162.717 30.628 157.641C33.1084 152.558 36.1313 147.703 39.756 143.305C46.9407 134.453 56.2326 127.269 66.6764 122.403C71.9107 119.989 77.4511 118.233 83.1322 117.049C85.9708 116.434 88.8599 116.057 91.7643 115.757L96.1708 115.486L101.058 115.331L109.928 115.052L109.666 106.724C109.372 97.3806 110.338 87.9578 112.626 78.8065C112.602 80.1797 112.564 81.5529 112.571 82.9266L112.57 82.9514C112.577 81.5049 112.631 80.0598 112.671 78.6143C113.515 75.263 114.526 71.9459 115.735 68.6862C120.19 56.4501 127.122 45.0002 135.996 35.1059C144.906 25.2328 155.86 17.0807 168.043 11.0632C180.264 5.11508 193.77 1.45364 207.718 0.322174C193.773 -0.797394 179.39 1.00037 165.837 5.72388C152.234 10.3404 139.545 17.9644 128.695 27.7781C117.801 37.565 108.877 49.7104 102.63 63.232C97.6332 74.1221 94.3879 85.9059 93.0826 97.9423C92.3226 97.9408 91.5597 97.9409 90.8062 97.9508C87.2102 98.082 83.6043 98.3297 80.0266 98.8637C72.8682 99.8927 65.8075 101.782 59.0449 104.464C52.2958 107.178 45.8374 110.661 39.8477 114.848C33.8713 119.056 28.4226 124.016 23.5494 129.498C13.7992 140.46 6.65868 153.856 3.01064 168.206C1.17573 175.376 0.172571 182.766 0.016524 190.164C-0.127634 197.563 0.67539 204.999 2.27103 212.258C5.4321 226.801 12.1704 240.554 21.6397 252.041C31.1402 263.489 43.2391 272.836 56.9257 278.798C63.7437 281.818 70.9298 284.003 78.261 285.32C80.093 285.661 81.9304 285.959 83.7812 286.159C85.626 286.42 87.4797 286.556 89.3349 286.682C91.1882 286.855 93.0464 286.832 94.9026 286.889L97.4677 286.905L99.9115 286.898L106.132 286.845C121.536 286.992 136.941 287.055 152.345 286.943C188.572 286.733 224.799 285.783 261.026 284.245L261.848 284.213C262.024 284.205 262.191 284.165 262.365 284.147C273.377 283.831 284.39 283.502 295.403 283.124L314.952 282.403L319.865 282.215C321.631 282.093 323.398 282.055 325.154 281.847C328.666 281.471 332.163 280.97 335.597 280.183C349.394 277.269 362.23 270.832 372.894 262.097C394.457 244.714 406.084 216.918 403.835 190.815Z" fill="url(#paint0_linear_3682_31000)" fillOpacity="0.5" />
          <defs>
            <linearGradient id="paint0_linear_3682_31000" x1="-0.000319174" y1="143.5" x2="404.111" y2="143.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="#072D42" />
              <stop offset="0.38769" stopColor="#44A5E0" />
              <stop offset="1" stopColor="#44A5E0" />
            </linearGradient>
          </defs>
        </svg>
        <h3 className={classNames(styles.emptyData, { [styles.client]: isClient })}>
          <FormattedMessage id="emptyWordCloud" />
        </h3>
      </div>
    );
  }

  const heightProps = {};

  if (isMobile) {
    heightProps.height = words.length > 50 ? 800 : 650;
  }

  return (
    <>
      <TagCloud
        printRef={printRef}
        words={words}
        className={styles.cloud}
        style={{
          fontFamily: 'Mukta',
          padding: 12,
          ...heightProps,
        }}
      >
        { words.map((wordData) => {
          const { word, count } = wordData;
          const fullIncludeString = word === searchString;
          const index = getWordIndex(rangesConfig, count);
          const wordStyles = getWordStyles(index, isClient, isMobile);

          return (
            <div
              onClick={onClick(word)}
              key={word}
              style={wordStyles}
              className={classNames(
                styles.word,
                { [styles.searchInclude]: fullIncludeString },
              )}
            >
              {word}
            </div>
          );
        }) }
      </TagCloud>
      <RangeWords ranges={rangesConfig} getRangeStyles={getWordStyles} />
    </>
  );
};

export default React.memo(WordCloud);
